import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import { PreferenceFamily } from '@/creditflowresearch/domain/Preferences/PreferenceSource';
import type { IRegionAccessControl } from '@/creditflowresearch/domain/Region/selectors/hasRegionAccess';
import { EMERGING_MARKET_ID } from '@/creditflowresearch/domain/Market/constants';
import { MarketSegmentGroupType } from '@creditflowresearch/user-plan/vo/IMarketSegmentSelection';
import type { ISegmentScope } from '@/creditflowresearch/domain/Segment/selectors/selectSegmentVisibility';
import {
  selectAccessibleMappedMarketIds,
  selectAccessibleMappedRegionIds,
  selectScopedSegmentIds,
  toMarketAbbreviations,
  toMarketIds,
  toRegionAbbreviations,
  toSegmentAbbreviations,
} from '@/creditflowresearch/application/Preferences/selectAccessibleMappedIds';

export type TPreferenceQueryTarget =
  | { family: PreferenceFamily.ABS }
  | { family: PreferenceFamily.EM }
  | { family: PreferenceFamily.CORPORATE, productId: string, fallbackMarkets: string };

export interface IPreferenceQueryParts {
  regions?: string,
  markets?: string,
  segments?: string,
}

export const toSegmentScope = (target: TPreferenceQueryTarget): ISegmentScope | null => {
  switch (target.family) {
    case PreferenceFamily.CORPORATE:
      return { id: target.productId, type: MarketSegmentGroupType.Product };
    case PreferenceFamily.EM:
      return { id: EMERGING_MARKET_ID, type: MarketSegmentGroupType.Market };
    default:
      return null;
  }
};

const omitEmpty = (parts: IPreferenceQueryParts): IPreferenceQueryParts => Object
  .entries(parts)
  .reduce((kept, [key, value]) => (value ? { ...kept, [key]: value } : kept), {});

/**
 * The query parts a saved preference contributes to a target page, as abbreviations. A part the
 * preference says nothing about is omitted, so every caller keeps its own default for it —
 * spread these over the defaults rather than replacing them.
 *
 * Every surface that links to a market overview goes through here: the page's server-side
 * defaults, the header nav link, and (for segments) the login redirect. Keeping one answer is
 * what stops the three drifting apart.
 */
export const selectPreferenceQueryParts = (
  preferences: Preferences | null,
  accessControl: IRegionAccessControl,
  target: TPreferenceQueryTarget,
): IPreferenceQueryParts => {
  const scope = toSegmentScope(target);

  if (target.family === PreferenceFamily.CORPORATE) {
    const markets = toMarketAbbreviations(
      selectAccessibleMappedMarketIds(preferences, accessControl),
    );
    const marketIds = toMarketIds(markets || target.fallbackMarkets);

    return omitEmpty({
      markets,
      segments: scope
        ? toSegmentAbbreviations(selectScopedSegmentIds(preferences, scope, marketIds))
        : '',
    });
  }

  const regions = toRegionAbbreviations(
    selectAccessibleMappedRegionIds(preferences, accessControl, target.family),
  );

  return omitEmpty({
    regions,
    segments: scope
      ? toSegmentAbbreviations(selectScopedSegmentIds(preferences, scope, [EMERGING_MARKET_ID]))
      : '',
  });
};
